import { environment } from '../../../environments/environment';

/**
 * Per voicebot-portal's `getBrandAssetDomain()`, a brand's primary domain usually
 * matches its asset folder on the static CDN — with a handful of explicit overrides.
 */
const ASSET_DOMAIN_OVERRIDES: Record<string, string> = {
  'soldan.de': 'telefonservice.soldan.de',
  'app.officeclub.com': 'officeclub.com',
};

export function getBrandAssetDomain(domain: string | null | undefined): string | null {
  if (!domain) return null;
  const normalized = domain.trim().toLowerCase();
  if (!normalized) return null;
  return ASSET_DOMAIN_OVERRIDES[normalized] ?? normalized;
}

export type BrandLogoVariant = 'thumb' | 'hero';

/**
 * Builds the CDN URL for a brand logo. Always returns `logo_default.svg` — it's
 * the one file every brand reliably ships (some folders like `audelia.com` and
 * `officeclub.com` don't have a `logo_small.svg`). The `variant` parameter stays
 * for the call-site to pick the right on-page size via CSS.
 *
 * Returns `null` if the brand has no resolvable asset domain. The CDN is public.
 * On 404, callers silently remove the element per AD-16 (no fallback image).
 */
export function getBrandLogoUrl(
  domain: string | null | undefined,
  _variant: BrandLogoVariant = 'thumb',
): string | null {
  const assetDomain = getBrandAssetDomain(domain);
  if (!assetDomain) return null;

  const base = environment.staticCdnUrl;
  if (!base) return null;

  return `${base}/web-components/global/images/${assetDomain}/logo_default.svg`;
}

/**
 * CDN URL for a SUB-brand logo. Sub-brand assets live in a sub-folder under the
 * parent brand's asset folder, keyed by the sub-brand name lowercased — mirrors
 * EVA's `presentation-dashboard` (`…/images/{assetDomain}/{subBrand}/logo_default.svg`).
 *
 * Returns `null` if the parent domain or sub-brand name can't be resolved. The
 * folder may not exist for every sub-brand — callers (e.g. `cmt-brand-logo`) drop
 * the `<img>` on 404, same as brand logos (no fallback, AD-16).
 */
export function getSubBrandLogoUrl(
  parentDomain: string | null | undefined,
  subBrandName: string | null | undefined,
  _variant: BrandLogoVariant = 'thumb',
): string | null {
  const assetDomain = getBrandAssetDomain(parentDomain);
  if (!assetDomain) return null;

  const slug = subBrandName?.trim().toLowerCase();
  if (!slug) return null;

  const base = environment.staticCdnUrl;
  if (!base) return null;

  return `${base}/web-components/global/images/${assetDomain}/${slug}/logo_default.svg`;
}
